import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/ui/Layout';
import HomeScreen from './pages/home/HomeScreen';
import EditorPage from './pages/editor/EditorPage';
import HeaderEditorPage from './pages/editor/HeaderEditorPage';
import FooterEditorPage from './pages/editor/FooterEditorPage';
import CollectionListPage from './pages/collections/CollectionListPage';
import MediaPage from './pages/media/MediaPage';
import SettingsPage from './pages/settings/SettingsPage';
import SitePage from './pages/site/SitePage';
import SeoPage from './pages/seo/SeoPage';
import SubmissionsPage from './pages/submissions/SubmissionsPage';
import BookingsPage from './pages/bookings/BookingsPage';
import ReputationPage from './pages/reputation/ReputationPage';
import QrCodesPage from './pages/qr/QrCodesPage';
import ChatbotPage from './pages/chatbot/ChatbotPage';
import AccountPage from './pages/account/AccountPage';
import SetupPage from './pages/SetupPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OnboardingWizard from './pages/onboarding/OnboardingWizard';
import OnboardingCompletedScreen from './pages/onboarding/OnboardingCompletedScreen';
import GettingStartedPage from './pages/getting-started/GettingStartedPage';
import { api } from './lib/api';

type AppState = 'loading' | 'needs-setup' | 'needs-login' | 'needs-signup' | 'onboarding' | 'just-onboarded' | 'ready';

export default function App() {
  const [state, setState] = useState<AppState>('loading');

  // Resuelve a qué pantalla mandar al usuario: setup global → auth → wizard → panel.
  const refresh = useCallback(async () => {
    try {
      const { needsSetup } = await api.setup.check();
      if (needsSetup) { setState('needs-setup'); return; }
      const me = await api.auth.me();
      if (!me.user) { setState('needs-login'); return; }
      setState(me.needsOnboarding ? 'onboarding' : 'ready');
    } catch {
      setState('needs-login');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-ui-bg flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-ui-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state === 'needs-setup') {
    return <SetupPage onComplete={() => setState('needs-login')} />;
  }

  if (state === 'needs-login') {
    return <LoginPage onLogin={refresh} onSwitchToSignup={() => setState('needs-signup')} />;
  }

  if (state === 'needs-signup') {
    // Tras el alta el usuario ya queda logueado en onboarding.
    return <SignupPage onComplete={() => setState('onboarding')} onSwitchToLogin={() => setState('needs-login')} />;
  }

  if (state === 'onboarding') {
    return <OnboardingWizard onComplete={() => setState('just-onboarded')} />;
  }

  if (state === 'just-onboarded') {
    return <OnboardingCompletedScreen onEnter={() => setState('ready')} />;
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/editor/header" element={<HeaderEditorPage />} />
        <Route path="/editor/footer" element={<FooterEditorPage />} />
        <Route path="/editor/:pageId" element={<EditorPage />} />
        <Route element={<Layout />}>
          <Route index element={<HomeScreen />} />
          <Route path="pages/*" element={<Navigate to="/site?tab=paginas" replace />} />
          {/* Motor de colecciones genéricas — reemplaza a las rutas typed properties/agents/posts */}
          <Route path="getting-started" element={<GettingStartedPage />} />
          <Route path="c/:key" element={<CollectionListPage />} />
          <Route path="media" element={<MediaPage />} />
          <Route path="submissions" element={<SubmissionsPage />} />
          <Route path="bookings" element={<BookingsPage />} />
          <Route path="reputation" element={<ReputationPage />} />
          <Route path="chatbot" element={<ChatbotPage />} />
          <Route path="qr" element={<QrCodesPage />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="site/*" element={<SitePage />} />
          <Route path="seo/*" element={<SeoPage />} />
          <Route path="settings/*" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
